import React from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

export default function FinancialGoals({ goals, loading }) {
  const goalList = goals || [];

  const getDaysLeft = (deadline) => {
    if (!deadline) return null;
    const diff = new Date(deadline) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    return new Date(deadline)?.toLocaleString('default', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-[#F8FAFC]">Financial Goals</h3>
          <p className="text-xs text-[#94A3B8] mt-0.5">Track your progress</p>
        </div>
        <Button variant="ghost" size="sm" iconName="Plus" iconPosition="left">Add Goal</Button>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4]?.map((i) => (
            <div key={i} className="h-28 bg-[#1E3A5F] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : goalList?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Target" size={32} color="#475569" />
          <p className="text-sm text-[#475569] mt-2">No goals yet. Complete onboarding to set up your goals.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {goalList?.map((goal) => {
            const target = parseFloat(goal?.target_amount) || 1;
            const current = parseFloat(goal?.current_amount) || 0;
            const pct = Math.min((current / target) * 100, 100);
            const color = goal?.color || '#06B6D4';
            return (
              <div
                key={goal?.id}
                className="bg-[#0A1628] border border-[rgba(6,182,212,0.08)] rounded-lg p-4 hover:border-[rgba(6,182,212,0.2)] transition-all duration-250 cursor-pointer group"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
                    <Icon name={goal?.icon || 'Target'} size={18} color={color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-sm text-[#F8FAFC] truncate">{goal?.title}</p>
                    <p className="text-xs text-[#94A3B8]">Due {formatDeadline(goal?.deadline)}</p>
                  </div>
                  <span className="font-data text-xs font-semibold whitespace-nowrap" style={{ color }}>
                    {pct?.toFixed(0)}%
                  </span>
                </div>
                <div className="h-1.5 bg-[#1E3A5F] rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-data text-xs text-[#94A3B8]">
                    ${current?.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-[#475569]">/ ${target?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </span>
                  <span className="text-xs text-[#94A3B8]">
                    <span className="font-data" style={{ color }}>${parseFloat(goal?.monthly_contribution || 0)?.toFixed(0)}</span>/mo needed
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}